import React, { useEffect, useState } from 'react';
import { Calendar, Clock, MapPin, ChevronRight } from 'lucide-react';
import { subscribeToBookings } from '../../services/firestore';
import { BOOKING_STATUS } from '../../types/booking';
import { formatDistanceToNow, isToday, isTomorrow, format } from 'date-fns';
import { id } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const UpcomingBookings = ({ limit = 5 }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribeToBookings((data) => {
      // Filter for upcoming bookings
      const now = new Date();
      const upcoming = data
        .filter(booking => {
          // Only show confirmed and pending deposit bookings
          if (
            booking.status === BOOKING_STATUS.CANCELLED ||
            booking.status === BOOKING_STATUS.COMPLETED ||
            booking.status === BOOKING_STATUS.NO_SHOW
          ) {
            return false;
          }

          // Check if event date is in the future
          const eventDate = booking.eventDate?.toDate
            ? booking.eventDate.toDate()
            : new Date(booking.eventDate);

          return eventDate >= now;
        })
        .sort((a, b) => {
          const dateA = a.eventDate?.toDate ? a.eventDate.toDate() : new Date(a.eventDate);
          const dateB = b.eventDate?.toDate ? b.eventDate.toDate() : new Date(b.eventDate);
          return dateA - dateB;
        })
        .slice(0, limit);

      setBookings(upcoming);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [limit]);

  const formatDate = (date) => {
    try {
      const eventDate = date?.toDate ? date.toDate() : new Date(date);

      if (isToday(eventDate)) {
        return 'Hari ini';
      } else if (isTomorrow(eventDate)) {
        return 'Besok';
      } else {
        return format(eventDate, 'dd MMM yyyy', { locale: id });
      }
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (date) => {
    try {
      const eventDate = date?.toDate ? date.toDate() : new Date(date);
      const now = new Date();
      const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));

      if (daysUntil <= 0) return 'Hari ini';
      if (daysUntil === 1) return 'Besok';
      return `Dalam ${daysUntil} hari`;
    } catch {
      return 'Segera';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case BOOKING_STATUS.PENDING_DEPOSIT:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case BOOKING_STATUS.CONFIRMED:
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      default:
        return 'bg-[#D4AF7A]/20 text-[#D4AF7A] border-[#D4AF7A]/30';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case BOOKING_STATUS.PENDING_DEPOSIT:
        return 'Menunggu DP';
      case BOOKING_STATUS.CONFIRMED:
        return 'Dikonfirmasi';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">Booking Mendatang</h3>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-[#D4AF7A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#F7F5F2] mb-4">Booking Mendatang</h3>
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-[#D4AF7A]/50 mx-auto mb-3" />
          <p className="text-[#F7F5F2]/70">Tidak ada booking mendatang</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#F7F5F2]">Booking Mendatang</h3>
        <button
          onClick={() => navigate('/admin/bookings')}
          className="text-xs text-[#D4AF7A] hover:text-[#C9A882] transition-colors"
        >
          Lihat Semua
        </button>
      </div>

      <div className="space-y-3">
        {bookings.map((booking) => (
          <div
            key={booking.id}
            onClick={() => navigate(`/admin/bookings?booking=${booking.id}`)}
            className="group p-4 rounded-lg bg-white/5 border border-[#D4AF7A]/10 hover:border-[#D4AF7A]/30 transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-[#F7F5F2] truncate">
                    {booking.clientName}
                  </h4>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatusColor(booking.status)}`}>
                    {getStatusLabel(booking.status)}
                  </span>
                </div>
                <p className="text-xs text-[#F7F5F2]/70 mb-1">
                  {booking.bookingNumber}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#D4AF7A]/50 group-hover:text-[#D4AF7A] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-[#F7F5F2]/80">
                <Calendar className="w-3.5 h-3.5 text-[#D4AF7A]" />
                <span>{formatDate(booking.eventDate)}</span>
                <span className="text-[#F7F5F2]/50">•</span>
                <Clock className="w-3.5 h-3.5 text-[#D4AF7A]" />
                <span>{booking.eventTime || 'TBD'}</span>
              </div>

              <div className="flex items-center gap-2 text-xs text-[#F7F5F2]/60">
                <MapPin className="w-3.5 h-3.5 text-[#D4AF7A]" />
                <span className="truncate">{booking.eventLocation || 'Lokasi belum ditentukan'}</span>
              </div>

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#D4AF7A]/10">
                <span className="text-xs text-[#D4AF7A] font-medium">
                  {formatTime(booking.eventDate)}
                </span>
                {booking.totalAmount && (
                  <span className="text-xs text-[#F7F5F2]/80">
                    {new Intl.NumberFormat('id-ID', {
                      style: 'currency',
                      currency: 'IDR',
                      minimumFractionDigits: 0
                    }).format(booking.totalAmount)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UpcomingBookings;
